import { LocalDate, YearMonth, DateRange, RangePreset, ComparePreset } from './types';
import { monthOf, shiftYearMonth } from './dateHelpers';
import { rangeFromPreset, MovableRangePreset } from './rangeFromPreset';
import { deriveB } from './deriveB';

// 'A' es siempre el periodo bajo examen, 'B' siempre la referencia.
type PickerTarget = 'A' | 'B';

// Posición visual del panel, no su identidad — en Mode=Derived los dos
// paneles muestran [panelMonth, panelMonth+1] y ninguno es "B"; en
// Mode=Custom 'left' es el panel A y 'right' es el panel B.
type PanelSlot = 'left' | 'right';

interface PickerState {
  rangePreset: RangePreset;
  comparePreset: ComparePreset;
  a: DateRange;
  manualB: DateRange | null; // la B elegida a mano. Se aparca, no se destruye.
  panelMonth: YearMonth;     // panel izquierdo; Derived muestra [panelMonth, +1]
  panelBMonth: YearMonth;    // solo relevante en Custom
  draft: { target: PickerTarget; anchor: LocalDate } | null;
  // Extensión sobre la interfaz del plan: HOVER_DAY necesita dónde guardar el
  // preview. No se persiste nada más allá de la sesión de selección.
  hover: LocalDate | null;
}

type PickerAction =
  | { type: 'SELECT_RANGE_PRESET'; preset: MovableRangePreset; today: LocalDate }
  | { type: 'SELECT_COMPARE_PRESET'; preset: ComparePreset }
  | { type: 'CLICK_DAY'; target: PickerTarget; day: LocalDate }
  | { type: 'HOVER_DAY'; day: LocalDate | null }
  | { type: 'NAV_MONTH'; slot: PanelSlot; delta: number }
  | { type: 'SET_MONTH'; slot: PanelSlot; month: YearMonth }
  | { type: 'APPLY' }
  | { type: 'CANCEL'; restoreTo: PickerState };

// Única fuente de verdad para B — nunca se persiste salvo que sea manual.
const resolveB = (state: PickerState): DateRange =>
  state.comparePreset === 'custom'
    ? (state.manualB ?? deriveB(state.a, 'prev-period'))
    : deriveB(state.a, state.comparePreset);

// Defectos al abrir: últimas dos semanas comparadas contra el periodo
// anterior de igual longitud.
const createInitialPickerState = (today: LocalDate): PickerState => {
  const rangePreset: RangePreset = '2w';
  const comparePreset: ComparePreset = 'prev-period';
  const a = rangeFromPreset(rangePreset, today);
  const seededB = deriveB(a, 'prev-period');
  return {
    rangePreset,
    comparePreset,
    a,
    manualB: null,
    panelMonth: shiftYearMonth(monthOf(a.end), -1),
    panelBMonth: monthOf(seededB.start),
    draft: null,
    hover: null
  };
};

const selectManualRange = (state: PickerState, target: PickerTarget, range: DateRange, draft: PickerState['draft']): PickerState =>
  target === 'A'
    ? { ...state, a: range, rangePreset: 'custom', draft, hover: null }
    : { ...state, manualB: range, comparePreset: 'custom', draft, hover: null };

const pickerReducer = (state: PickerState, action: PickerAction): PickerState => {
  switch(action.type) {
    case 'SELECT_RANGE_PRESET': {
      const a = rangeFromPreset(action.preset, action.today);
      // manualB no se toca aquí sea cual sea comparePreset: si sigue activa
      // una regla derivada, B se recalcula solo al leer resolveB(); si el
      // modo es custom, manualB es de quien la eligió.
      return {
        ...state,
        rangePreset: action.preset,
        a,
        panelMonth: shiftYearMonth(monthOf(a.end), -1),
        draft: null,
        hover: null
      };
    }

    case 'SELECT_COMPARE_PRESET': {
      if(action.preset === 'custom') {
        const manualB = state.manualB ?? deriveB(state.a, 'prev-period');
        return {
          ...state,
          comparePreset: 'custom',
          manualB,
          panelBMonth: monthOf(manualB.start),
          draft: null,
          hover: null
        };
      }
      // Opción derivada: el layout colapsa a un calendario, manualB queda
      // intacta — aparcada, no destruida.
      return { ...state, comparePreset: action.preset, draft: null, hover: null };
    }

    case 'CLICK_DAY': {
      const { target, day } = action;
      if(!state.draft || state.draft.target !== target) {
        // primer clic: nuevo borrador, colapsa a un único día
        return selectManualRange(state, target, { start: day, end: day }, { target, anchor: day });
      }
      // segundo clic: confirma [min, max] — se permite seleccionar hacia atrás
      const anchor = state.draft.anchor;
      const range: DateRange = day < anchor ? { start: day, end: anchor } : { start: anchor, end: day };
      return selectManualRange(state, target, range, null);
    }

    case 'HOVER_DAY':
      return { ...state, hover: action.day };

    case 'NAV_MONTH': {
      if(state.comparePreset === 'custom') {
        return action.slot === 'left'
          ? { ...state, panelMonth: shiftYearMonth(state.panelMonth, action.delta) }
          : { ...state, panelBMonth: shiftYearMonth(state.panelBMonth, action.delta) };
      }
      // Derived: los dos paneles están enlazados, cualquiera desplaza la pareja
      return { ...state, panelMonth: shiftYearMonth(state.panelMonth, action.delta) };
    }

    case 'SET_MONTH': {
      if(state.comparePreset === 'custom') {
        return action.slot === 'left'
          ? { ...state, panelMonth: action.month }
          : { ...state, panelBMonth: action.month };
      }
      // Derived: el desplegable derecho fija panelMonth − 1 para que los dos
      // paneles sigan consecutivos
      return {
        ...state,
        panelMonth: action.slot === 'left' ? action.month : shiftYearMonth(action.month, -1)
      };
    }

    case 'APPLY':
      return { ...state, draft: null, hover: null };

    case 'CANCEL':
      return { ...action.restoreTo, draft: null, hover: null };
  }
};

// Qué debe pintar el calendario para `target` ahora mismo: el rango
// confirmado, o el preview [min(anchor, hover), max(anchor, hover)] mientras
// hay un borrador en curso sobre ese mismo eje — §08. No confirma nada.
const resolveDisplayRange = (state: PickerState, target: PickerTarget): DateRange => {
  const confirmed = target === 'A' ? state.a : resolveB(state);
  if(!state.draft || state.draft.target !== target || state.hover === null) {
    return confirmed;
  }
  const { anchor } = state.draft;
  const hover = state.hover;
  return hover < anchor ? { start: hover, end: anchor } : { start: anchor, end: hover };
};

export { PickerState, PickerAction, PickerTarget, PanelSlot, resolveB, resolveDisplayRange, createInitialPickerState, pickerReducer };
